import React, { useState } from 'react'
import { useCookies } from 'react-cookie'

import { useAppSelector } from 'app-redux/hooks'
import { useCanEditDeck } from 'app-redux/deck/selectors'

import { CardType, EDH_FORMATS } from 'types/deck'

import MakeCompanionButton from 'components/card/cardForm/makeCompanionButton'
import SetDeckImageButton from 'components/card/cardForm/setDeckImageButton'
import AddToCollectionButton from 'components/collection/AddToCollectionButton/AddToCollectionButton'
import AddToOtherDeck from 'components/card/cardForm/addToOtherDeck'
import Categories from 'components/card/cardForm/categories/categories'
import CardModalCategoriesInfo from 'components/tooltips/CardModalCategoriesInfo'
import ToggleFoilButton from 'components/card/cardForm/toggleFoilButton'
import CustomCmcDropdown from 'components/card/cardForm/customCmcDropdown'
import QuantityInput from 'components/card/cardForm/quantityInput'
import EditionOptions from 'components/card/cardForm/editionOptions'
import CardEditionsGridOverlay from '../CardEditionsGridOverlay'
import LockPersonalCategoriesButton from './lockPersonalCategoriesButton'
import ColorLabelDropdown from './colorLabelDropdown'
import AddToNewDeck from './addToNewDeck'
import PhatButton from 'components/formElements/PhatButton'
import Link from 'components/elements/Link'
import CopyCustomCard from './copyCustomCard'
import ReportModal from 'components/misc/ReportModal'
import AddToPinsButton from 'components/card/cardForm/addToPins'
import OTagQuickOptions from './OTagQuickOptions'
import { ToCategoryButton, ToCommanderButton } from 'components/card/cardForm/toCategoryButtons'

import { NextPrevOptions } from 'components/elements/Overlay/CardDetailsOverlay'

import { generateDeckUrl } from 'utils/deckSeo'

import styles from './cardForm.module.scss'

type Props = {
  card: CardType
  onCardUpdated: (card: CardType) => void
  multiEdit?: boolean
  flipped?: boolean
  disabled?: boolean
  nextPrevControls?: {
    previous: NextPrevOptions
    next: NextPrevOptions
  }
}

const CardForm = ({ card, onCardUpdated, multiEdit, flipped = false, nextPrevControls, disabled }: Props) => {
  const starttingRef = React.useRef<HTMLButtonElement>(null)

  const [{ tbId: userId, settings: accountSettings = {} }] = useCookies(['tbId', 'settings'])
  const { show_custom_uploads: showCustomUploads = false } = accountSettings

  const canEditDeck = useCanEditDeck()

  const format = useAppSelector(state => state.deck.format)
  const deckId = useAppSelector(state => state.deck.id)
  const deckName = useAppSelector(state => state.deck.name)

  const [viewReportModal, setViewReportModal] = useState(false)

  return (
    <div className={styles.container}>
      {!!card.customCard && `${card.customCard?.ownerId}` !== `${userId}` && (
        <div className={styles.customCardBlock}>
          <p>You are viewing a custom card</p>
          {!showCustomUploads && (
            <p>
              If you wish to view any uploaded images for custom cards or alters, you can do so by enabling that setting
              via your <Link href={userId ? '/accountSettings/site-settings' : '/register'}>Account Settings</Link>
            </p>
          )}

          {showCustomUploads && <PhatButton onClick={() => setViewReportModal(true)}>Report custom card</PhatButton>}
        </div>
      )}

      <button className={styles.focusFirstButton} ref={starttingRef} />

      {canEditDeck && (
        <div className={styles.quantityCommanderRow}>
          <QuantityInput
            className={`${styles.quantityRow} ${multiEdit ? styles.removeExtraMargins : ''}`}
            quantity={card.qty}
            onQuantityUpdate={qty => onCardUpdated({ ...card, qty })}
            disabled={disabled}
          />

          <div className={styles.commanderCompanion}>
            {EDH_FORMATS.includes(format) && (
              <ToCommanderButton card={card} onCardUpdate={onCardUpdated} disabled={disabled} />
            )}
            <MakeCompanionButton card={card} onCardUpdated={onCardUpdated} disabled={disabled} />
          </div>
        </div>
      )}

      <div className={styles.row}>
        {!multiEdit && (
          <EditionOptions
            label="Printing"
            card={card}
            disabled={!!card.customCard || disabled}
            className={styles.editionSelection}
            onEditionChanged={onCardUpdated}
            nextPrevControls={nextPrevControls}
          />
        )}

        <ToggleFoilButton
          className={multiEdit ? styles.fullWidth : ''}
          triggerClassName={multiEdit ? styles.fullWidth : ''}
          card={card}
          onCardUpdate={onCardUpdated}
          disabled={disabled}
        />
      </div>

      <div className={styles.categroyAndExtras}>
        <div className={styles.left}>
          <div className={styles.prettyBox}>
            <CardModalCategoriesInfo />
            <Categories card={card} onCardChange={onCardUpdated} disabled={disabled} />
            {/* <div className={styles.divider} /> */}
            {canEditDeck && (
              <>
                <label>Quick category options:</label>
                <div className={styles.sideMaybeRow}>
                  {!multiEdit && (
                    <ToCategoryButton
                      icon="backward"
                      card={card}
                      categoryName={card.globalCategories[0] || card.defaultCategory || card.typeCategory}
                      onCardUpdate={onCardUpdated}
                      disabled={disabled}
                    />
                  )}
                  <ToCategoryButton
                    card={card}
                    categoryName="Sideboard"
                    onCardUpdate={onCardUpdated}
                    disabled={disabled}
                  />

                  <ToCategoryButton
                    card={card}
                    categoryName="Maybeboard"
                    onCardUpdate={onCardUpdated}
                    disabled={disabled}
                  />

                  <OTagQuickOptions card={card} disabled={disabled} onCardUpdate={onCardUpdated} />
                </div>
              </>
            )}
          </div>
          {canEditDeck && !multiEdit && (
            <div>
              <LockPersonalCategoriesButton
                card={card}
                disabled={disabled}
                onCardUpdated={onCardUpdated}
                className={styles.lockPersonalCategories}
              />
            </div>
          )}
        </div>

        <div className={styles.right}>
          <label>Other options:</label>

          <div className={styles.extraOptionsGrid}>
            {canEditDeck && <ColorLabelDropdown card={card} onCardUpdate={onCardUpdated} disabled={disabled} />}
            {canEditDeck && (
              <CustomCmcDropdown card={card} onCardUpdate={onCardUpdated} multiEdit={multiEdit} disabled={disabled} />
            )}

            {!multiEdit && (
              <>
                {canEditDeck && <SetDeckImageButton card={card} flipped={flipped} disabled={disabled} />}

                {!!userId && !card?.customCard && (
                  <AddToCollectionButton
                    card={card}
                    onCardUpdated={onCardUpdated}
                    disabled={!!card?.customCard?.id || disabled}
                  />
                )}

                {!!userId && <AddToOtherDeck card={card} key={card.id} disabled={disabled} />}
                {!!userId && !card?.customCard && (
                  <AddToPinsButton card={card} onCardUpdated={onCardUpdated} disabled={disabled} />
                )}

                {!!userId && !!card?.customCard && `${card?.customCard?.ownerId}` !== `${userId}` && (
                  <CopyCustomCard card={card} disabled={disabled} />
                )}

                {!canEditDeck && !card?.customCard?.id && <AddToNewDeck card={card} disabled={disabled} />}
              </>
            )}
          </div>
        </div>
      </div>

      <ReportModal
        type="Custom card"
        open={viewReportModal}
        onClose={() => setViewReportModal(false)}
        url={generateDeckUrl(deckId, deckName)}
        header="Report custom card"
      />
    </div>
  )
}

export default CardForm
